import { getSupabaseClient } from '../supabaseClient';

// Maps UI field types to DB enum values
const mapFieldType = (uiType) => {
  switch (uiType) {
    case 'numeric':
      return 'numeric';
    case 'radio':
    case 'select':
      return 'enum';
    case 'checkbox':
      return 'boolean';
    case 'date':
    case 'time':
    case 'text':
    default:
      return 'string';
  }
};

// Builds headers object based on auth config
const buildHeaders = (auth) => {
  const headers = { 'Content-Type': 'application/json' };
  if (!auth || auth.type === 'none') return headers;
  if (auth.type === 'bearer') {
    headers['Authorization'] = `Bearer ${auth.token}`;
  } else if (auth.type === 'basic') {
    headers['Authorization'] = `Basic ${btoa(`${auth.username}:${auth.password}`)}`;
  } else if (auth.type === 'apikey') {
    headers[auth.keyName || 'X-API-Key'] = auth.keyValue;
  }
  return headers;
};

// NEW Helper function to convert HTML to plain text
const convertHtmlToPlainText = (htmlString) => {
  if (!htmlString) return '';
  // This should ideally run in an environment where DOM is available.
  // For server-side or non-browser contexts, a different library would be needed.
  if (typeof document !== 'undefined') {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlString;
    return tempDiv.textContent || tempDiv.innerText || '';
  }
  // Fallback for non-DOM environments (might not be perfect)
  return htmlString.replace(/<[^>]+>/g, ''); 
};

// NEW Helper function to transform UI mentions to DB ID placeholders
const transformMentionsForSave = (text, allQuestionsList) => {
  if (!text || !allQuestionsList || allQuestionsList.length === 0) {
    return text;
  }
  const questionTextToIdMap = new Map(allQuestionsList.map(q => [q.text, q.id]));

  return text.replace(/\{\{([^}]+?)\}\}/g, (match, questionText) => {
    const trimmedQuestionText = questionText.trim(); // Ensure no leading/trailing spaces affect lookup
    const questionId = questionTextToIdMap.get(trimmedQuestionText);
    return questionId ? `{{${questionId}}}` : match; // If not found, keep original
  });
};

// NEW Helper function to transform DB ID placeholders to UI mentions
export const transformMentionsForLoad = (text, allQuestionsList) => {
  if (!text || !allQuestionsList || allQuestionsList.length === 0) {
    return text;
  }
  // Create a map from question ID to question text for efficient lookup
  const questionIdToTextMap = new Map(allQuestionsList.map(q => [q.id, q.text]));

  // Regex to find {{uuid}} (or any non-curly-brace content within {{}})
  return text.replace(/\{\{([^}]+?)\}\}/g, (match, questionId) => {
    const questionText = questionIdToTextMap.get(questionId.trim()); // Trim ID just in case
    // If a corresponding question text is found, replace; otherwise, keep the original placeholder
    return questionText ? `{{${questionText}}}` : match; 
  });
};

// Helper to save fields and enums for a complex action (REWRITTEN FOR UPSERT LOGIC)
const saveFieldsAndEnums = async (supabase, complexActionId, questionsFromUI, isUpdatingThisAction) => {
  if (!Array.isArray(questionsFromUI)) {
    // console.warn('No questions provided to saveFieldsAndEnums or questions is not an array');
    return;
  }

  // 0. Pre-process UI questions to assign dbOrder (relative order for children)
  const processedQuestionsFromUI = [];
  let topLevelOrderCounter = 0;
  const parentToChildOrderCounter = {};

  const uiQualifyingQuestions = questionsFromUI.filter(q => q.isQualifying);
  const uiOrderQuestions = questionsFromUI.filter(q => !q.isQualifying);

  uiQualifyingQuestions.forEach((q, index) => {
    processedQuestionsFromUI.push({ ...q, dbOrder: index, tempId: q.id }); // Keep original UI id as tempId
  });

  const topLevelOrderQuestions = uiOrderQuestions.filter(q => !q.parentFieldId);
  topLevelOrderQuestions.forEach(q => {
    processedQuestionsFromUI.push({ ...q, dbOrder: topLevelOrderCounter++, tempId: q.id });
  });

  uiOrderQuestions.forEach(q => {
    if (q.parentFieldId) {
      if (parentToChildOrderCounter[q.parentFieldId] === undefined) {
        parentToChildOrderCounter[q.parentFieldId] = 0;
      }
      processedQuestionsFromUI.push({ ...q, dbOrder: parentToChildOrderCounter[q.parentFieldId]++, tempId: q.id });
    }
  });
  
  // Map UI question temporary IDs to their processed object for later reference
  const uiQuestionMapByTempId = new Map(processedQuestionsFromUI.map(q => [q.tempId, q]));

  // 1. Fetch existing DB fields if updating
  let existingDbFieldsMap = new Map();
  if (isUpdatingThisAction) {
    const { data: existingFields, error: fetchErr } = await supabase
      .from('brain_complex_actions_fields')
      .select('*')
      .eq('brain_complex_actions_id', complexActionId);
    if (fetchErr) {
      console.error('Error fetching existing fields:', fetchErr);
      throw fetchErr;
    }
    existingFields.forEach(f => existingDbFieldsMap.set(f.id, f));
  }

  const uiFieldIds = new Set(processedQuestionsFromUI.map(q => q.id).filter(id => typeof id === 'string' && id.length === 36)); // Filter for actual UUIDs

  // 2. Determine fields to delete, update, or insert
  const fieldsToDeleteIds = [];
  if (isUpdatingThisAction) {
    existingDbFieldsMap.forEach(dbField => {
      if (!uiFieldIds.has(dbField.id)) {
        fieldsToDeleteIds.push(dbField.id);
      }
    });
  }

  // 3. Perform Deletions (Fields and their Enums)
  if (fieldsToDeleteIds.length > 0) {
    // Delete enums for these fields first
    const { error: deleteEnumsErr } = await supabase
      .from('brain_complex_actions_fields_enum')
      .delete()
      .in('complex_actions_fields_id', fieldsToDeleteIds);
    if (deleteEnumsErr && deleteEnumsErr.code !== 'PGRST204') { // Ignore "No Content"
      console.error('Error deleting enums for removed fields:', deleteEnumsErr);
      // Potentially throw, but for now, log and continue to attempt field deletion
    }

    const { error: deleteFieldsErr } = await supabase
      .from('brain_complex_actions_fields')
      .delete()
      .in('id', fieldsToDeleteIds);
    if (deleteFieldsErr) {
      console.error('Error deleting fields:', deleteFieldsErr);
      throw deleteFieldsErr;
    }
    // console.log('Deleted fields:', fieldsToDeleteIds);
  }

  // 4. Process Upserts (Inserts and Updates) in passes
  // Pass 1: Insert new fields (those without a DB UUID id) and update existing fields (those with a DB UUID id)
  // We need to map temporary UI IDs to new DB IDs for parent_field_id resolution later.
  const tempIdToDbIdMap = new Map(); // For newly inserted fields
  const fieldsToUpdate = [];
  const fieldsToInsert = [];

  for (const uiQuestion of processedQuestionsFromUI) {
    const fieldPayload = {
      brain_complex_actions_id: complexActionId,
      type: mapFieldType(uiQuestion.type),
      question: uiQuestion.text,
      order: uiQuestion.dbOrder,
      is_qualifying: uiQuestion.isQualifying || false,
      // parent_field_id and criteria will be handled in Pass 2 after all IDs are known
      // is_editable: uiQuestion.isEditable, // Add if this field exists and needs to be saved
    };

    if (typeof uiQuestion.id === 'string' && uiQuestion.id.length === 36 && existingDbFieldsMap.has(uiQuestion.id)) {
      // This is an existing field, prepare for update
      const existingField = existingDbFieldsMap.get(uiQuestion.id);
      // Check if update is actually needed by comparing salient properties
      if (existingField.type !== fieldPayload.type ||
          existingField.question !== fieldPayload.question ||
          existingField.order !== fieldPayload.order ||
          existingField.is_qualifying !== fieldPayload.is_qualifying) {
        fieldsToUpdate.push({ id: uiQuestion.id, payload: fieldPayload });
      } else {
        // No change in core properties, but parent/criteria might change in Pass 2
        // or enums might change. For now, we ensure it's in the map for Pass 2.
        tempIdToDbIdMap.set(uiQuestion.tempId, uiQuestion.id); // Map its tempId to its existing DB ID
      }
       // Keep existing DB ID for parent mapping
      tempIdToDbIdMap.set(uiQuestion.tempId, uiQuestion.id);
    } else {
      // This is a new field
      fieldsToInsert.push({ tempId: uiQuestion.tempId, payload: fieldPayload });
    }
  }

  // Perform Inserts
  if (fieldsToInsert.length > 0) {
    const insertPayloads = fieldsToInsert.map(f => f.payload);
    const { data: insertedFields, error: insertErr } = await supabase
      .from('brain_complex_actions_fields')
      .insert(insertPayloads)
      .select('id'); // Select only ID

    if (insertErr) {
      console.error('Error inserting new fields:', insertErr);
      throw insertErr;
    }
    // Map temporary UI IDs to newly created DB IDs
    insertedFields.forEach((dbField, index) => {
      tempIdToDbIdMap.set(fieldsToInsert[index].tempId, dbField.id);
    });
    // console.log('Inserted new fields:', insertedFields.length);
  }
  
  // Perform Updates for core field properties (excluding parent_field_id and criteria for now)
  if (fieldsToUpdate.length > 0) {
    const updatePromises = fieldsToUpdate.map(f =>
      supabase.from('brain_complex_actions_fields').update(f.payload).eq('id', f.id)
    );
    const updateResults = await Promise.all(updatePromises);
    updateResults.forEach(result => {
      if (result.error) {
        console.error('Error updating field core properties:', result.error);
        // Decide whether to throw
      }
    });
    // console.log('Updated fields (core props):', fieldsToUpdate.length);
  }

  // Pass 2: Update parent_field_id and criteria for all relevant fields (newly inserted or existing)
  const parentCriteriaUpdatePromises = [];
  for (const uiQuestion of processedQuestionsFromUI) {
    const currentFieldDbId = tempIdToDbIdMap.get(uiQuestion.tempId) || uiQuestion.id; // Get DB ID (new or existing)
    if (!currentFieldDbId || (typeof currentFieldDbId === 'string' && currentFieldDbId.startsWith('temp_'))) {
        // console.warn(`Skipping parent/criteria update for question '${uiQuestion.text}' as its DB ID is not resolved.`);
        continue;
    }

    let parentFieldDbId = null;
    if (uiQuestion.parentFieldId) {
      // ParentFieldId from UI could be a tempId (for a new parent) or a DB UUID (for an existing parent)
      parentFieldDbId = tempIdToDbIdMap.get(uiQuestion.parentFieldId) || uiQuestion.parentFieldId;
       if (typeof parentFieldDbId === 'string' && parentFieldDbId.startsWith('temp_')) {
         // console.warn(`Parent ID ${uiQuestion.parentFieldId} for question '${uiQuestion.text}' could not be resolved to a DB ID. Skipping parent linkage.`);
         parentFieldDbId = null; // Cannot link
       }
    }

    const updatePayload = {};
    let needsUpdate = false;

    const existingFieldForParentCriteria = existingDbFieldsMap.get(currentFieldDbId);

    if (parentFieldDbId !== (existingFieldForParentCriteria?.parent_field_id || null)) {
        updatePayload.parent_field_id = parentFieldDbId;
        needsUpdate = true;
    }
    // Basic criteria check (deep comparison might be needed for robustness)
    if (JSON.stringify(uiQuestion.criteria) !== JSON.stringify(existingFieldForParentCriteria?.criteria || null)) {
        updatePayload.criteria = uiQuestion.criteria;
        needsUpdate = true;
    }
    
    if (needsUpdate && Object.keys(updatePayload).length > 0) {
      parentCriteriaUpdatePromises.push(
        supabase.from('brain_complex_actions_fields').update(updatePayload).eq('id', currentFieldDbId)
      );
    }
  }

  if (parentCriteriaUpdatePromises.length > 0) {
    const parentCriteriaResults = await Promise.all(parentCriteriaUpdatePromises);
    parentCriteriaResults.forEach(result => {
      if (result.error) console.error('Error updating field parent/criteria:', result.error);
    });
    // console.log('Updated fields (parent/criteria):', parentCriteriaUpdatePromises.length);
  }

  // 5. Handle Enums with granular upsert logic
  const allDbFieldsAfterUpsert = [];
  const { data: currentFieldsInDb, error: fetchCurrentFieldsError } = await supabase
    .from('brain_complex_actions_fields')
    .select('id, type') // Select id and type to check if it should have enums
    .eq('brain_complex_actions_id', complexActionId);

  if (fetchCurrentFieldsError) {
    console.error('Error fetching current fields from DB for enum processing:', fetchCurrentFieldsError);
    throw fetchCurrentFieldsError;
  }
  currentFieldsInDb.forEach(f => allDbFieldsAfterUpsert.push(f));


  for (const uiQuestion of processedQuestionsFromUI) {
    const fieldDbId = tempIdToDbIdMap.get(uiQuestion.tempId) || uiQuestion.id;
    if (!fieldDbId || String(fieldDbId).startsWith('temp_')) {
      // console.warn(`Skipping enum processing for question '${uiQuestion.text}' as its DB ID is not resolved.`);
      continue;
    }

    const isEnumFieldInUi = (uiQuestion.type === 'radio' || uiQuestion.type === 'select');
    const dbField = allDbFieldsAfterUpsert.find(f => f.id === fieldDbId);
    const wasEnumFieldInDb = dbField && (mapFieldType(dbField.type) === 'enum'); // mapFieldType needed if db stores 'enum'

    // Get existing enums for this specific field
    let existingEnumsForField = [];
    if (isUpdatingThisAction || wasEnumFieldInDb) { // Only fetch if updating or it was an enum previously
      const { data: enumsData, error: enumsErr } = await supabase
        .from('brain_complex_actions_fields_enum')
        .select('id, enum')
        .eq('complex_actions_fields_id', fieldDbId);
      if (enumsErr) {
        console.error(`Error fetching existing enums for field ${fieldDbId}:`, enumsErr);
        // Decide if we should throw or continue
      } else {
        existingEnumsForField = enumsData || [];
      }
    }

    const uiOptions = uiQuestion.options || [];

    if (isEnumFieldInUi) {
      // Field is currently an enum type in UI
      const enumsToInsert = [];
      const enumValuesToDelete = [];

      // Determine enums to insert
      for (const uiOpt of uiOptions) {
        if (!existingEnumsForField.some(dbEnum => dbEnum.enum === uiOpt)) {
          enumsToInsert.push({ complex_actions_fields_id: fieldDbId, enum: uiOpt });
        }
      }

      // Determine enums to delete
      for (const dbEnum of existingEnumsForField) {
        if (!uiOptions.includes(dbEnum.enum)) {
          enumValuesToDelete.push(dbEnum.id);
        }
      }

      if (enumValuesToDelete.length > 0) {
        const { error: deleteErr } = await supabase
          .from('brain_complex_actions_fields_enum')
          .delete()
          .in('id', enumValuesToDelete);
        if (deleteErr) console.error(`Error deleting enums for field ${fieldDbId}:`, deleteErr);
        // else console.log(`Deleted ${enumValuesToDelete.length} enums for field ${fieldDbId}`);
      }

      if (enumsToInsert.length > 0) {
        const { error: insertErr } = await supabase
          .from('brain_complex_actions_fields_enum')
          .insert(enumsToInsert);
        if (insertErr) console.error(`Error inserting enums for field ${fieldDbId}:`, insertErr);
        // else console.log(`Inserted ${enumsToInsert.length} enums for field ${fieldDbId}`);
      }
    } else if (wasEnumFieldInDb) {
      // Field was an enum in DB but is no longer an enum in UI, so delete all its old enums
      if (existingEnumsForField.length > 0) {
        const idsToDelete = existingEnumsForField.map(e => e.id);
        const { error: deleteErr } = await supabase
          .from('brain_complex_actions_fields_enum')
          .delete()
          .in('id', idsToDelete);
        if (deleteErr) console.error(`Error clearing old enums for field ${fieldDbId} (type changed):`, deleteErr);
        // else console.log(`Cleared ${idsToDelete.length} old enums for field ${fieldDbId} (type changed)`);
      }
    }
  }
  // console.log('Finished granular enum processing for action ID:', complexActionId);
};

// Helper to save action-specific data
const saveActionSpecificData = async (supabase, complexActionId, toolConnection, allQuestionsList) => {
    const actionType = toolConnection?.actionType || 'api';

    // Transform applicable fields
    const transformedSmsMessage = toolConnection.smsMessage ? transformMentionsForSave(toolConnection.smsMessage, allQuestionsList) : '';
    const transformedEmailSubject = toolConnection.emailSubject ? transformMentionsForSave(toolConnection.emailSubject, allQuestionsList) : '';
    const transformedEmailMessage = toolConnection.emailMessage ? transformMentionsForSave(toolConnection.emailMessage, allQuestionsList) : '';
    const transformedTransferMessage = toolConnection.transferMessage ? transformMentionsForSave(toolConnection.transferMessage, allQuestionsList) : '';
    // For API body, be cautious. If it's pure JSON, direct replacement of {{text}} might be fine if IDs are simple strings.
    // If IDs can have special chars or if the intent is to embed as non-string values, more complex parsing/handling for JSON is needed.
    // For now, assuming simple string replacement is the goal for consistency.
    const transformedApiBody = toolConnection.apiBody ? transformMentionsForSave(toolConnection.apiBody, allQuestionsList) : '';

    if (actionType === 'api') {
      let apiBodyJson = null;
      if (transformedApiBody.trim()) {
        try {
          // If API body is expected to be JSON, try parsing the transformed version
          apiBodyJson = JSON.parse(transformedApiBody); 
        } catch (e) {
          // If parsing fails, it might be a template string not intended to be pure JSON after transformation.
          // Or, the transformation resulted in invalid JSON.
          // Fallback: save the transformed string as is if it was not meant to be parseable JSON.
          // This decision depends on how the API body is ultimately used.
          // For now, let's assume if it fails to parse, it's a templated string.
          apiBodyJson = transformedApiBody; 
          // console.warn(`API body for action ${complexActionId} was not valid JSON after mention transformation, saving as string:`, transformedApiBody);
        }
      }

        const { error } = await supabase.from('brain_complex_actions_api').insert({
            complex_action_id: complexActionId,
            url: toolConnection.apiEndpoint || '',
            method: toolConnection.apiMethod || 'POST',
            headers: Object.keys(toolConnection.apiHeaders || {}).length ? toolConnection.apiHeaders : null,
            body: apiBodyJson // Use the potentially transformed and parsed/string body
        });
        if (error) throw error;
    } else if (actionType === 'email') {
        const { data: emailRow, error: emailErr } = await supabase.from('brain_complex_actions_email').insert({
            complex_action_id: complexActionId,
            subject: transformedEmailSubject,
            message: transformedEmailMessage,
            use_contact_email: toolConnection.emailUseContactEmail || false
        }).select('id').single();
        if (emailErr) throw emailErr;

        if (toolConnection.emailUseContactEmail && toolConnection.emailAddresses?.length) {
            const addrRecords = toolConnection.emailAddresses.map(addr => ({
                complex_action_email_id: emailRow.id, // Assuming FK column name
                email_address: addr
            }));
            // Assuming 'brain_complex_actions_email_addresses' table exists
            const { error: addrErr } = await supabase.from('brain_complex_actions_email_addresses').insert(addrRecords);
            if (addrErr) throw addrErr;
        }
    } else if (actionType === 'sms') {
        const { data: smsRow, error: smsErr } = await supabase.from('brain_complex_actions_sms').insert({
            complex_action_id: complexActionId,
            message: transformedSmsMessage, // Use transformed message
            use_contact_phone: toolConnection.smsUseContactPhone || false
        }).select('id').single();
        if (smsErr) throw smsErr;

        if (toolConnection.smsUseContactPhone && toolConnection.smsNumbers?.length) {
            const numRecords = toolConnection.smsNumbers.map(num => ({
                complex_action_sms_id: smsRow.id,
                phone_number: num
            }));
             // Assuming 'brain_complex_actions_sms_numbers' table exists
            const { error: numErr } = await supabase.from('brain_complex_actions_sms_numbers').insert(numRecords);
            if (numErr) throw numErr;
        }
    }
    // TODO: Add logic for 'transfer' if needed ('brain_complex_actions_transfer_call'?)
    // Ensure transformedTransferMessage is used if 'transfer' actions have a message field.
};

// Helper to delete fields and related data for a complex action
const deleteFieldsAndActionData = async (supabase, complexActionId, deleteAllFieldsAndEnums = true) => {
    if (deleteAllFieldsAndEnums) {
        // 1. Explicitly delete related enums and fields first.
        try {
            // Find field IDs associated with the complex action
            const { data: fieldsToDelete, error: findFieldsErr } = await supabase
                .from('brain_complex_actions_fields')
                .select('id')
                .eq('brain_complex_actions_id', complexActionId);
            
            if (findFieldsErr) {
                console.error("Error finding fields to delete:", findFieldsErr);
                throw findFieldsErr;
            }

            if (fieldsToDelete && fieldsToDelete.length > 0) {
                const fieldIdsToDelete = fieldsToDelete.map(f => f.id);

                // Delete enums linked to these fields
                const { error: deleteEnumsErr } = await supabase
                    .from('brain_complex_actions_fields_enum')
                    .delete()
                    .in('complex_actions_fields_id', fieldIdsToDelete);
                    
                if (deleteEnumsErr && deleteEnumsErr.code !== 'PGRST204') {
                    console.error("Error deleting field enums:", deleteEnumsErr);
                    throw deleteEnumsErr;
                }

                // Delete the fields themselves
                const { error: deleteFieldsErr } = await supabase
                    .from('brain_complex_actions_fields')
                    .delete()
                    .eq('brain_complex_actions_id', complexActionId);
                    
                if (deleteFieldsErr && deleteFieldsErr.code !== 'PGRST204') {
                     console.error("Error deleting fields:", deleteFieldsErr);
                     throw deleteFieldsErr;
                }
                // console.log('Deleted fields & enums for complexActionId:', complexActionId);
            }
        } catch (error) {
            console.error("Error during explicit field/enum deletion:", error);
            throw error;
        }
    }

    // 2. Delete existing action-specific data (all types) - This is always done for the specific action ID
    // Assuming relevant tables exist like 'brain_complex_actions_api', 'brain_complex_actions_email', etc.
    // Delete API
    const { error: apiDelErr } = await supabase.from('brain_complex_actions_api').delete().eq('complex_action_id', complexActionId);
    if (apiDelErr && apiDelErr.code !== 'PGRST204') { console.error("Error deleting API data:", apiDelErr); throw apiDelErr; } // Ignore "No Content"

    // Delete Email (handling cascade for addresses)
    // Note: Cascade might already handle deletion of addresses if FK is set up correctly on brain_complex_actions_email_addresses
    const { error: emailDelErr } = await supabase.from('brain_complex_actions_email').delete().eq('complex_action_id', complexActionId);
    if (emailDelErr && emailDelErr.code !== 'PGRST204') { console.error("Error deleting Email data:", emailDelErr); throw emailDelErr; }

    // Delete SMS (handling cascade for numbers)
     // Note: Cascade might already handle deletion of numbers if FK is set up correctly on brain_complex_actions_sms_numbers
    const { error: smsDelErr } = await supabase.from('brain_complex_actions_sms').delete().eq('complex_action_id', complexActionId);
    if (smsDelErr && smsDelErr.code !== 'PGRST204') { console.error("Error deleting SMS data:", smsDelErr); throw smsDelErr; }

    // Delete Transfer data if exists
    // const { error: transferDelErr } = await supabase.from('brain_complex_actions_transfer_call').delete().eq('complex_action_id', complexActionId);
    // if (transferDelErr && transferDelErr.code !== 'PGRST204') { console.error("Error deleting Transfer data:", transferDelErr); throw transferDelErr; }
};


// Main function to save pathway (now handles variants)
export const savePathway = async (dataToSave, complexActionIdToUpdate = null) => {
  const isOverallSettingsUpdate = !!dataToSave.settings_id; // If settings_id exists, we are updating settings
  const supabase = getSupabaseClient();

  // Get user
  const { data: { user }, error: userErr } = await supabase.auth.getUser();
  if (userErr || !user) throw userErr || new Error('User not authenticated');
  const accountId = user.id;

  let settingsId = dataToSave.settings_id;
  const categoryId = dataToSave.category_id;
  if (!categoryId) {
      throw new Error("Category ID is required to save a pathway.");
  }
  const allQuestionsList = [...(dataToSave.qualifyingQuestions || []), ...(dataToSave.orderQuestions || [])];

  // --- Upsert brain_settings ---
  const settingsPayload = {
    category_id: categoryId,
    type: 'complex_action',
    name: dataToSave.name,
    description: convertHtmlToPlainText(dataToSave.description || null),
    triggers: dataToSave.triggers || [],
  };

  if (isOverallSettingsUpdate && settingsId) {
    const { error: setUpdateErr } = await supabase
      .from('brain_settings')
      .update(settingsPayload)
      .eq('id', settingsId);
    if (setUpdateErr) { console.error("Settings Update Error:", setUpdateErr); throw setUpdateErr; }
  } else {
    const { data: settings, error: setInsertErr } = await supabase
      .from('brain_settings')
      .insert({ ...settingsPayload })
      .select()
      .single();
    if (setInsertErr) { console.error("Settings Insert Error:", setInsertErr); throw setInsertErr; }
    settingsId = settings.id; 
  }

  // --- Handle Groupings, Assignments, and Actions ---
  const frontendToDbGroupIdMap = {}; 
  // 1. Fetch existing groupings for this settingsId to compare (needed for both modes to handle deletions/updates)
  const { data: existingDbGroupingsData } = await supabase
      .from('brain_groupings')
      .select('id, name')
      .eq('settings_id', settingsId);
  const existingDbGroupings = existingDbGroupingsData || [];
  const existingDbGroupIds = existingDbGroupings.map(g => g.id);

  // 2. Determine groupings to add/update and delete
  const frontendGroupings = dataToSave.groupings || [];

  const frontendRealGroupIds = frontendGroupings
      .filter(g => !g.isNew && !String(g.id).startsWith('temp_'))
      .map(g => g.id);

  const groupingsToDelete = existingDbGroupings.filter(dbG => !frontendRealGroupIds.includes(dbG.id));

  // 3. Delete removed groupings 
  if (groupingsToDelete.length > 0) {
      const idsToDelete = groupingsToDelete.map(g => g.id);
      const { error: deleteGrpErr } = await supabase
          .from('brain_groupings')
          .delete()
          .in('id', idsToDelete);
      if (deleteGrpErr) { console.error("Delete Groupings Error:", deleteGrpErr); /* Potentially throw */ }
  }

  // 4. Upsert current groupings from the payload & map IDs
  for (const group of frontendGroupings) {
      const isNewFrontendGroup = group.isNew || String(group.id).startsWith('temp_');
      const groupPayload = { settings_id: settingsId, name: group.name };

      if (!isNewFrontendGroup && existingDbGroupIds.includes(group.id)) {
           const existingGroup = existingDbGroupings.find(g => g.id === group.id);
           if (existingGroup && existingGroup.name !== group.name) {
               const { error: updateGrpErr } = await supabase
                   .from('brain_groupings')
                   .update({ name: group.name })
                   .eq('id', group.id);
               if (updateGrpErr) { console.error("Update Grouping Name Error:", updateGrpErr); throw updateGrpErr; }
           }
           frontendToDbGroupIdMap[group.id] = group.id;
      } else {
          const { data: newGroup, error: insertGrpErr } = await supabase
              .from('brain_groupings')
              .insert(groupPayload)
              .select('id') // Only select ID
              .single();
          if (insertGrpErr) { console.error("Insert Grouping Error:", insertGrpErr); throw insertGrpErr; }
          frontendToDbGroupIdMap[group.id] = newGroup.id;
      }
  }

  // 5. Update assignments and actions for each *final* DB grouping ID
  for (const frontendGroupId in frontendToDbGroupIdMap) {
    const dbGroupId = frontendToDbGroupIdMap[frontendGroupId];
    if (!dbGroupId) continue;

    // 5a. Update Locations for this DB Group ID
    const assignedLocationIds = dataToSave.assignments[frontendGroupId] || [];
    // Delete existing assignments for this group
    const { error: deleteAssignErr } = await supabase
        .from('brain_grouping_locations')
        .delete()
        .eq('grouping_id', dbGroupId);
    if (deleteAssignErr && deleteAssignErr.code !== 'PGRST204') {
        console.error(`Delete Assignments Error (Group ${dbGroupId}):`, deleteAssignErr);
        throw deleteAssignErr;
    }
    // Insert new assignments
    if (assignedLocationIds.length > 0) {
        const assignmentRecords = assignedLocationIds.map(locId => ({ grouping_id: dbGroupId, location_id: locId }));
        const { error: insertAssignErr } = await supabase.from('brain_grouping_locations').insert(assignmentRecords);
        if (insertAssignErr) { console.error(`Insert Assignments Error (Group ${dbGroupId}):`, insertAssignErr); throw insertAssignErr; }
    }

    // 5b. Upsert Complex Action and its data
    const variantData = dataToSave.variantsData[frontendGroupId];
    if (!variantData || !variantData.toolConnection) {
        // console.warn(`No variant data or toolConnection for frontend group ID ${frontendGroupId}. Skipping action upsert.`);
        continue;
    }

    // Fetch existing action for this group to determine if it's an update or action_type change
    const { data: existingActionForGroup, error: fetchExistingActionErr } = await supabase
        .from('brain_complex_actions')
        .select('id, action_type')
        .eq('settings_id', settingsId)
        .eq('grouping_id', dbGroupId)
        .maybeSingle(); // Use maybeSingle to not error if no action exists yet

    if (fetchExistingActionErr) {
        console.error('Error fetching existing action for group:', dbGroupId, fetchExistingActionErr);
        throw fetchExistingActionErr;
    }

    const complexActionPayload = {
        settings_id: settingsId,
        grouping_id: dbGroupId,
        name: dataToSave.name, 
        description: convertHtmlToPlainText(dataToSave.description || null),
        action_type: variantData.toolConnection?.actionType || 'api'
    };

    const { data: upsertedComplexAction, error: upsertActionErr } = await supabase
        .from('brain_complex_actions')
        .upsert(complexActionPayload, { onConflict: 'settings_id, grouping_id' })
        .select('id') // only need id
        .single();

    if (upsertActionErr) { console.error(`Upsert Complex Action Error (Group ${dbGroupId}):`, upsertActionErr); throw upsertActionErr; }
    const currentComplexActionId = upsertedComplexAction.id;

    // Determine if we should delete old fields and action-specific data
    const wasNewActionInsert = !existingActionForGroup; // True if no action existed before upsert
    const actionTypeChanged = existingActionForGroup && existingActionForGroup.action_type !== complexActionPayload.action_type;
    
    // Delete all data (fields, enums, action-specific) if it's a new insert OR action type changed
    const shouldDeleteAllOldData = wasNewActionInsert || actionTypeChanged;
    
    // Always delete action-specific tables first (API, SMS, EMAIL)
    // Then, conditionally delete fields/enums based on shouldDeleteAllOldData
    await deleteFieldsAndActionData(supabase, currentComplexActionId, shouldDeleteAllOldData);

    // Save/Update fields and enums.
    // The last argument tells saveFieldsAndEnums if it should perform an upsert (true) or a fresh insert (false)
    // It should upsert if we are NOT deleting all old data.
    await saveFieldsAndEnums(supabase, currentComplexActionId, allQuestionsList, !shouldDeleteAllOldData);
    
    // Save new action-specific data (API, Email, SMS details)
    await saveActionSpecificData(supabase, currentComplexActionId, variantData.toolConnection, allQuestionsList);
  }

  return { settingsId, primaryComplexActionId: complexActionIdToUpdate }; // Return primary ID if it was an update context
};
